import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { AccountInfo } from "@azure/msal-browser";
import { login } from "../redux/reducers/auth.reducer";
import { addToast } from "../redux/reducers/ui.reducer";
import { UsersService } from "../../UsersModule/services/users";
import httpService from "../utils/api";

// Shared by Auth and TimeoutLogic: silently (re)acquires an MSAL access
// token, resolves the Azure AD object id to TheHub's internal Users.Id
// (ASUITE11-4388), and dispatches `login`. Callers differ in what happens
// right after a successful login, so that's passed in as a callback.
// Returned as a useCallback so callers that put it in a useEffect dependency
// array (Auth) don't re-run that effect on every render.
export const useReacquireToken = (
  getRoles: () => Promise<void>,
  onLoginSuccess?: () => void
) => {
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();

  const acquireTokenAndLogin = useCallback(() => {
    const accessTokenRequest = {
      scopes: ["api://894e8b20-1b64-479c-b4a6-6e4fd7e1757f/Billing.Access"],
      account: accounts[0] as AccountInfo,
    };
    instance
      .acquireTokenSilent(accessTokenRequest)
      .then(async (response) => {
        // ASUITE11-3876
        if (response.expiresOn && response.expiresOn < new Date()) {
          dispatch(
            addToast({
              mode: "error",
              message: "Microsoft token is expired. Wait until login in again.",
            })
          );
          instance.acquireTokenRedirect(accessTokenRequest);
        }

        // ASUITE11-4388: accounts[0].localAccountId is the Azure AD object id,
        // not TheHub's internal Users.Id — resolve the real one before letting
        // the rest of the app treat activeUserId as a backend user id.
        httpService.setToken(response.accessToken);
        let profile;
        try {
          profile = await UsersService.getUserProfileInfoByAzureObjectId(
            accounts[0].localAccountId
          );
        } catch {
          dispatch(
            addToast({
              mode: "error",
              message:
                "We couldn't find your user profile in TheHub. Please contact IT.",
            })
          );
          return;
        }

        dispatch(
          login({
            accessToken: response.accessToken,
            expiresOn: (response.expiresOn ?? new Date()).getTime(),
            activeUserId: profile.id,
            activeUserName: accounts[0].name,
            activeUserEmail: accounts[0].username,
          })
        );
        getRoles();
        onLoginSuccess?.();
      })
      // ASUITE11-2744: this line going to redirect number of times to get correct token
      .catch(() => {
        instance.acquireTokenRedirect(accessTokenRequest);
      });
  }, [instance, accounts, dispatch, getRoles, onLoginSuccess]);

  return acquireTokenAndLogin;
};
